import { Injectable } from '@angular/core';
import { resetStores } from '@datorama/akita';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export type RealtimeEngine = 'v1' | 'v2';

/**
 * Arbitro unico del motore realtime (v1 legacy / v2). E' l'UNICA fonte di verita' su quale motore
 * gira: sia SocketIoService (v1) sia RealtimeV2RuntimeService (v2) leggono di qui invece di decidere
 * autonomamente da authQuery.user$.
 *
 * Decisione al boot: presa dal valore persistito (__REALTIME_ENGINE), sincrono e indipendente
 * dall'utente ri-idratato. NON deriva da authStore.user, che puo' essere stantio nello storage del
 * browser (ri-idratato a true mentre il backend segnala false) e causerebbe un loop di reload.
 *
 * Cambio motore: solo deliberato via reconcile(), protetto da un cooldown anti-rimbalzo che spezza il
 * ping-pong tra due sorgenti realtime discordi (es. push BE corretto vs storage stantio).
 */
@Injectable({ providedIn: 'root' })
export class RealtimeEngineService {
  private static readonly ENGINE_KEY = '__REALTIME_ENGINE';
  // Timestamp dell'ultimo switch: ignoro lo switch inverso entro la finestra.
  private static readonly SWITCH_TS_KEY = '__REALTIME_ENGINE_SWITCH_TS';
  private static readonly COOLDOWN_MS = 10_000;

  private readonly _engine$ = new BehaviorSubject<RealtimeEngine>(this.readPersisted());

  /** Motore attivo adesso. Stabile per tutta la vita della pagina. */
  readonly engine$ = this._engine$.pipe(distinctUntilChanged());

  get engine(): RealtimeEngine {
    return this._engine$.value;
  }

  isV2(): boolean {
    return this.engine === 'v2';
  }

  isV1(): boolean {
    return this.engine === 'v1';
  }

  private readPersisted(): RealtimeEngine {
    return localStorage.getItem(RealtimeEngineService.ENGINE_KEY) === 'v2' ? 'v2' : 'v1';
  }

  /**
   * Riconcilia il motore col valore desiderato (di norma derivato dal flag dell'utente).
   * - no-op se gia' allineato;
   * - no-op dentro il cooldown: spezza il loop quando una sorgente stantia rispinge il valore opposto
   *   subito dopo uno switch.
   * Quando procede: persiste la scelta, lascia salvare lo snapshot auth (callback), pulisce gli store
   * (tranne auth/ui) e ricarica per ripartire pulito sul motore corretto.
   */
  reconcile(desired: RealtimeEngine): void {
    if (desired === this.engine) {
      return;
    }

    const lastSwitch = Number(localStorage.getItem(RealtimeEngineService.SWITCH_TS_KEY) || 0);
    if (Date.now() - lastSwitch < RealtimeEngineService.COOLDOWN_MS) {
      console.warn('[REALTIME-ENGINE] switch a', desired, 'ignorato: dentro cooldown anti-rimbalzo');
      return;
    }

    console.log('[REALTIME-ENGINE] switch motore (reload)', this.engine, '->', desired);
    localStorage.setItem(RealtimeEngineService.ENGINE_KEY, desired);
    localStorage.setItem(RealtimeEngineService.SWITCH_TS_KEY, String(Date.now()));
    resetStores({ exclude: ['auth', 'ui'] });
    window.location.reload();
  }

  /**
   * Cambio motore IN-PLACE, senza reload: persiste la scelta e aggiorna engine$. Le subscription di
   * gating (runtime v2 e socket legacy v1) reagiscono spegnendo/accendendo il motore giusto.
   * Da usare quando NON ci sono viste di progetto montate (tipicamente al login, prima di entrare in
   * un progetto): evita il reload che, a sessione appena loggata, perderebbe l'auth non ancora flushata
   * da akita persistState. Per lo switch DENTRO un progetto serve invece reconcile() (reload + remount
   * pulito) per non far esplodere i template su resetStores.
   */
  setEngine(desired: RealtimeEngine): void {
    if (desired === this.engine) {
      return;
    }

    console.log('[REALTIME-ENGINE] switch motore (in-place)', this.engine, '->', desired);
    localStorage.setItem(RealtimeEngineService.ENGINE_KEY, desired);
    localStorage.setItem(RealtimeEngineService.SWITCH_TS_KEY, String(Date.now()));
    this._engine$.next(desired);
  }
}
